import React, { FC, useEffect, useRef, useState } from "react";
import injectSheet from "react-jss";
import { motion } from "framer-motion";
import Close from "Assets/images/close.svg";
import Portal from "HOC/Portal";
import { useSelector } from "react-redux";
import { areEqual } from "Utils/areEqual";
import styleSheet from "./styles";
import { focusFirstDescendant, focusLastDescendant } from "./focusUtils";

interface Props {
  onClose?: () => void;
  children: any;
  classes?: any;
}

const onKeyUp = (event, onClose = null) => {
  if (event.key === "Escape") {
    event.stopPropagation();
    if (onClose) onClose();
  }
};

const Modal: FC<Props> = (props: Props) => {
  const { children, onClose, classes } = props;
  const [isOpen, setIsOpen] = useState(true);
  const modalContainerRef = useRef();

  let hideClose = true;
  const { modules } = useSelector(
    (selectors: any) => selectors.locationModulesMatrix,
    areEqual
  );
  if (modules?.find((m) => m.moduleid === "registration_cif"))
    hideClose = false;

  const enhancedChildren = React.cloneElement(children, {
    ...children.props,
    closeModal: onClose,
  });

  useEffect(() => {
    const keyUpListener = (event) => onKeyUp(event, onClose);
    document.addEventListener("keyup", keyUpListener);
    return () => {
      document.removeEventListener("keyup", keyUpListener);
    };
  }, [onClose]);

  useEffect(() => {
    // Focus Modal on load
    if (modalContainerRef.current) {
      focusFirstDescendant(modalContainerRef.current);
    }
  }, []);

  // animate open and close //
  const modalAnimations = {
    open: {
      opacity: 1,
    },
    closed: {
      opacity: 0,
      transition: {
        onComplete: () => {
          if (onClose) onClose();
        },
      },
    },
  };

  return (
    <Portal>
      <div
        id="modal-backdrop-container"
        className={classes.modalBackdropContainer}
      >
        {/* This div is to trap focus in this modal. */}
        <div
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          onFocus={() => {
            if (modalContainerRef.current) {
              focusLastDescendant(modalContainerRef.current);
            }
          }}
        />

        <motion.div
          role="dialog"
          aria-modal="true"
          initial={{ opacity: 0 }}
          animate={isOpen ? "open" : "closed"}
          variants={modalAnimations}
          className={classes.container}
          ref={modalContainerRef}
        >
          {!hideClose && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
              onClick={() => setIsOpen(false)}
              className={classes.close}
              type="button"
              aria-label="Close"
            >
              <Close />
            </motion.button>
          )}
          {enhancedChildren}
        </motion.div>

        {/* This div is to trap focus in this modal. Because the modal can include an iframe for CIF registration form,
           this is the only way that have tried so far, that works well with chrome screen reader tabbing (for accessibility) */}
        <div
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          onFocus={() => {
            if (modalContainerRef.current) {
              focusFirstDescendant(modalContainerRef.current);
            }
          }}
        />
      </div>
    </Portal>
  );
};
Modal.defaultProps = {
  onClose: undefined,
  classes: {},
};
export default injectSheet(styleSheet)(Modal);
